import { useSyncExternalStore } from "react";
import { useRouter } from "next/router";

import useUser from "@/api/apis";
import { useWelcomeCouponIssuing } from "@/hooks/useWelcomeCouponIssuing";
import { useConsentStore } from "@/libs/consent";
import { GE_CHECKOUT_PATH } from "@/libs/global-e/checkout-marker";
import {
  isDismissedToday,
  useWelcomeCouponPopupStore,
  useWelcomeCouponSessionStore,
} from "@/store/welcome-coupon-popup-store";

/**
 * 신규 가입 쿠폰 팝업이 마운트되는 라우트. 지금은 메인뿐이다(`pages/index.tsx`).
 *
 * 미니 배지는 "팝업이 안 보일 때" 뜨므로 팝업이 어디에 떠 있는지를 알아야 한다. 배지는
 * `_app` 에 전역 마운트되어 팝업과 트리를 공유하지 않으니 라우트로 판정할 수밖에 없다.
 * → **팝업 마운트 위치를 옮기면 이 배열도 같이 고칠 것.** (안 고치면 메인에서 팝업과 배지가
 * 동시에 뜨거나, 반대로 팝업이 뜬 페이지에서 배지가 사라지지 않는다.)
 */
export const WELCOME_COUPON_POPUP_ROUTES: readonly string[] = ["/"];

/**
 * 결제 진행 화면. 여기서는 배지를 띄우지 않는다 — 결제 직전 이탈(로그인 이동)을 유도하는
 * 꼴이라 장바구니를 날리고 주문을 깬다. `router.pathname` 은 locale 이 벗겨진 **라우트 패턴**
 * 이므로 `/kr/checkout` 도 `/checkout` 으로 들어온다.
 */
const CHECKOUT_ROUTE_PREFIXES = ["/checkout", GE_CHECKOUT_PATH];

export const isCheckoutRoute = (pathname: string): boolean =>
  CHECKOUT_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const subscribeNothing = () => () => {};

/**
 * 신규 가입 쿠폰 제안(팝업 + 미니 배지)의 공통 노출 판정.
 *
 * 두 표면이 같은 캠페인·같은 조건을 쓰므로 게이팅을 한 곳에 모은다. 규칙이 두 벌이면
 * "팝업은 떴는데 배지는 안 뜨는" 조합이 조용히 생긴다.
 *
 * `offer` 를 함께 돌려주는 이유: 두 표면이 할인율·유효기간을 **화면에 쓴다.** 각자 훅을
 * 부르면 같은 순간에 서로 다른 값을 그릴 수 있다(리페치 타이밍이 다르다).
 */
export function useWelcomeCouponOffer() {
  const router = useRouter();
  const { user, isLoading } = useUser();

  // 지금 실제로 발급되는가 + 무슨 조건인가. mgmt 가 킬 스위치·활성·발급 창을 전부 흡수해
  // 판정한 결과다 — 게이팅이 없으면 받을 수 없는 혜택을 계속 약속하게 된다.
  const offer = useWelcomeCouponIssuing();

  const consentInitialized = useConsentStore((s) => s.initialized);
  const consentBannerOpen = useConsentStore((s) => s.showBanner);
  const consentSettingsOpen = useConsentStore((s) => s.showSettings);

  const dismissedUntil = useWelcomeCouponPopupStore((s) => s.dismissedUntil);
  const popupClosed = useWelcomeCouponSessionStore((s) => s.popupClosed);

  // 저장된 "오늘 보지 않기"·"이번 방문 닫기" 는 클라이언트에만 있어, 마운트 전에는 그리지
  // 않는다(hydration 불일치 방지). effect 안에서 setState 를 부르면 연쇄 렌더가 생기므로
  // useSyncExternalStore 로 판정한다.
  const hydrated = useSyncExternalStore(
    subscribeNothing,
    () => true,
    () => false,
  );

  /**
   * 캠페인·로그인·동의 기준의 노출 자격. 쿠키 동의를 끝낸 뒤에만 띄운다 — 첫 진입이면 동의
   * 배너가 반드시 함께 뜨는데, 모바일에서 배너가 화면 하단을 크게 차지해 겹친다. 법적 고지인
   * 배너를 가릴 수 없으니 배너가 닫힌 뒤로 순서를 미룬다. `initialized` 도 함께 보는 이유는
   * init() 이 effect 에서 돌아 직후 잠시 false 이고, 그때 `showBanner` 도 false 라서다.
   */
  const eligible =
    hydrated &&
    // `!== null` 로 boolean 으로 좁힌다 — 오퍼 객체를 그대로 `&&` 에 넣으면 `eligible` 이
    // 객체·null 이 되어 소비처의 `if (!eligible)` 는 통과하지만 타입이 흐려진다.
    offer !== null &&
    !isLoading &&
    !user &&
    consentInitialized &&
    !consentBannerOpen &&
    !consentSettingsOpen &&
    // "오늘 하루 보지 않기" 는 **두 표면 모두**에 걸린다. 팝업에만 걸면 체크하는 순간
    // `popupVisible` 이 false 가 되는데 그게 곧 배지가 뜨는 조건이라, 사용자의 유일한
    // 거절 의사가 오히려 배지를 켜고 24시간 내내 전 페이지에 같은 제안을 띄운다(#5115 계약 위반).
    !isDismissedToday(dismissedUntil);

  /** 지금 이 화면에 팝업이 실제로 떠 있는지. */
  const popupVisible =
    eligible &&
    WELCOME_COUPON_POPUP_ROUTES.includes(router.pathname) &&
    !popupClosed;

  return { eligible, popupVisible, offer };
}
